/**
 * @module services/zoteroService
 * @description Zotero API client service (facade)
 *
 * This is the main entry point for Zotero API interactions.
 * It provides a unified interface that combines all specialized services:
 *
 * - Authentication and API key validation
 * - Library and group management
 * - Collection CRUD operations
 * - Item CRUD operations
 * - Search functionality
 * - Export operations
 *
 * The implementation is split into focused modules in the ./zotero/ directory
 * for better maintainability and testability.
 *
 * @see https://www.zotero.org/support/dev/web_api/v3/start
 *
 * @example
 * import { zoteroService } from '@services/zoteroService';
 *
 * // Validate API key
 * const result = await zoteroService.validateApiKey('your-key');
 *
 * // Fetch items
 * const items = await zoteroService.getItems(libraryId, 'user', { limit: 50 });
 */

// Import from modular services
import { apiClient } from './zotero/apiClient';
import * as authService from './zotero/authService';
import * as libraryService from './zotero/libraryService';
import * as collectionService from './zotero/collectionService';
import * as itemService from './zotero/itemService';
import * as exportService from './zotero/exportService';

// Re-export types
export type { RequestConfig, PaginatedResponse } from './zotero/types';
export type { ExportFormat } from './zotero/exportService';

// =============================================================================
// ZOTERO SERVICE CLASS (Facade)
// =============================================================================

/**
 * Zotero API Service
 *
 * Unified interface combining all Zotero API operations.
 * Maintains backward compatibility with the original monolithic service.
 *
 * Supports two modes:
 * - Direct mode: API key stored locally, requests go directly to Zotero
 * - Proxy mode: API key stored in Spectral Cloud, requests go through Edge Function
 */
class ZoteroService {
  // ===========================================================================
  // AUTHENTICATION
  // ===========================================================================

  isAuthenticatedAsync = () => apiClient.isAuthenticatedAsync();

  validateApiKey = authService.validateApiKey;

  // ===========================================================================
  // LIBRARIES
  // ===========================================================================

  getUserLibrary = libraryService.getUserLibrary;
  getGroups = libraryService.getGroups;

  // ===========================================================================
  // COLLECTIONS
  // ===========================================================================

  getCollections = collectionService.getCollections;
  getCollection = collectionService.getCollection;
  createCollection = collectionService.createCollection;
  updateCollection = collectionService.updateCollection;
  deleteCollection = collectionService.deleteCollection;

  // ===========================================================================
  // ITEMS
  // ===========================================================================

  getItems = itemService.getItems;
  getItem = itemService.getItem;
  createItem = itemService.createItem;
  updateItem = itemService.updateItem;
  deleteItem = itemService.deleteItem;
  deleteItems = itemService.deleteItems;

  // ===========================================================================
  // TRASH
  // ===========================================================================

  getTrashItems = itemService.getTrashItems;
  emptyTrash = itemService.emptyTrash;

  // ===========================================================================
  // SEARCH
  // ===========================================================================

  searchItems = itemService.searchItems;

  // ===========================================================================
  // EXPORT
  // ===========================================================================

  static readonly EXPORT_FORMATS = exportService.EXPORT_FORMATS;
  static readonly CITATION_STYLES = exportService.CITATION_STYLES;

  exportItem = exportService.exportItem;
  exportItems = exportService.exportItems;
  getBibliography = exportService.getBibliography;
  exportCollection = exportService.exportCollection;

  // ===========================================================================
  // ITEM TEMPLATES
  // ===========================================================================

  getItemTemplate = itemService.getItemTemplate;
}

// =============================================================================
// EXPORTS
// =============================================================================

/** Singleton instance */
export const zoteroService = new ZoteroService();

/** Export class for testing */
export { ZoteroService };

export default zoteroService;
